import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { EventService, StateUtils } from '@spartacus/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { UserIdService } from '../../../../core/auth/user-auth/facade/user-id.service';
import { GlobalMessageService } from '../../../../core/global-message/facade/global-message.service';
import { GlobalMessageType } from '../../../../core/global-message/models/global-message.model';
import {
  AccountingCode,
  Cart,
  CartActionSourceData,
  CartType,
  CuttingRequest,
  OrderEntry,
  PalletFlagsRequest,
  ReelCuttingRequest,
  Unit,
} from '../../../../core/model';
import { PrincipalConfigurationService } from '../../../../core/user';
import { AsyncReturnService } from '../../../../shared';
import { AddCartEntriesFailEvent } from '../events';
import { CartFacade } from '../facades';
import { CartActions } from '../store/actions';
import { StateWithCart } from '../store/cart-state';
import { CartSelectors } from '../store/selectors';

@Injectable()
export class CartService implements CartFacade {
  constructor(
    private store: Store<StateWithCart>,
    private principalConfigurationService: PrincipalConfigurationService,
    private globalMessageService: GlobalMessageService,
    private userIdService: UserIdService,
    private eventService: EventService,
    private asyncReturnService: AsyncReturnService
  ) {}

  getCart(cartId: string): Observable<Cart> {
    return this.store.pipe(select(CartSelectors.getCartFactory(cartId)));
  }

  getCartEntity(cartId: string): Observable<StateUtils.ProcessesLoaderState<Cart>> {
    return this.store.pipe(select(CartSelectors.getCartEntityFactory(cartId)));
  }

  getEntries(cartId: string): Observable<OrderEntry[]> {
    return this.store.pipe(select(CartSelectors.getCartEntriesFactory(cartId)));
  }

  getEntryById(cartId: string, entryId: string): Observable<OrderEntry | undefined> {
    return this.store.pipe(select(CartSelectors.getCartEntryByIdFactory(cartId, entryId)));
  }

  isStable(cartId: string): Observable<boolean> {
    return this.store.pipe(select(CartSelectors.getCartIsStableFactory(cartId)), distinctUntilChanged());
  }

  loadCart({
    cartType,
    cartId,
    userId,
    extraData,
  }: {
    cartType: CartType;
    cartId: string;
    userId: string;
    extraData?: {
      active?: boolean;
    };
  }): void {
    this.store.dispatch(
      new CartActions.LoadCart({
        cartType,
        cartId,
        userId,
        extraData,
      })
    );
  }

  reloadCart(cartId: string, cartType: CartType, extraData?: { active: boolean }): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new CartActions.LoadCart({
            cartType,
            userId,
            cartId,
            extraData,
          })
        ),
      () => {}
    );
  }

  clearCarts(): void {
    this.store.dispatch(new CartActions.ClearCartsState());
  }

  addEntry(
    userId: string,
    cartType: CartType,
    cartId: string,
    articleCode: string,
    quantity: number,
    unit: Unit,
    orderCardCode?: string,
    customerMaterialNumber?: string,
    cartTemplate?: string
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.isAddCartEntryAllowed(cartId)
        .subscribe(() => {
          this.store.dispatch(
            new CartActions.AddCartEntry({
              userId,
              cartType,
              cartId,
              articleCode,
              quantity,
              unit,
              orderCardCode,
              customerMaterialNumber,
              cartTemplate,
              actionToken,
            })
          );
        })
        .unsubscribe()
    );
  }

  addA4SampleEntry(
    userId: string,
    cartId: string,
    productCode: string,
    grammage: number,
    color: string,
    quantity: number
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.isAddCartEntryAllowed(cartId)
        .subscribe(() => {
          this.store.dispatch(
            new CartActions.AddA4SampleCartEntry({
              cartType: CartType.sample,
              userId,
              cartId,
              productCode,
              grammage,
              color,
              quantity,
              actionToken,
            })
          );
        })
        .unsubscribe()
    );
  }

  quickAddEntry(
    userId: string,
    cartType: CartType,
    cartId: string,
    articleCode: string,
    quantity: number,
    unit: Unit,
    note?: string,
    customerMaterialNumber?: string
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.isAddCartEntryAllowed(cartId)
        .subscribe(() => {
          this.store.dispatch(
            new CartActions.QuickAddCartEntry({
              userId,
              cartType,
              cartId,
              articleCode,
              quantity,
              unit,
              note,
              customerMaterialNumber,
              actionToken,
            })
          );
        })
        .unsubscribe()
    );
  }

  addEntries(
    userId: string,
    cartType: CartType,
    cartId: string,
    entries: OrderEntry[],
    sourceData?: CartActionSourceData
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.isAddCartEntryAllowed(cartId, entries.length)
        .subscribe(() => {
          this.store.dispatch(
            new CartActions.AddCartEntries({
              userId,
              cartType,
              cartId,
              entries,
              sourceData,
              actionToken,
            })
          );
        })
        .unsubscribe()
    );
  }

  removeEntry(userId: string, cartType: CartType, cartId: string, entry: OrderEntry): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.RemoveCartEntry({
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  removeEntries(userId: string, cartType: CartType, cartId: string, entries: OrderEntry[]): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.RemoveCartEntries({
          userId,
          cartType,
          cartId,
          entries,
          actionToken,
        })
      )
    );
  }

  removeAllEntries(userId: string, cartType: CartType, cartId: string): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.getCart(cartId)
        .subscribe((cart) =>
          this.store.dispatch(
            new CartActions.RemoveAllCartEntries({
              userId,
              cartType,
              cartId,
              entries: cart?.entries,
              actionToken,
            })
          )
        )
        .unsubscribe()
    );
  }

  updateEntry(
    userId: string,
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    quantity?: number,
    unit?: Unit
  ): Observable<boolean> {
    if (quantity <= 0) {
      return this.removeEntry(userId, cartType, cartId, entry);
    } else {
      return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
        this.store.dispatch(
          new CartActions.UpdateCartEntry({
            userId,
            cartType,
            cartId,
            entry,
            quantity,
            unit,
            actionToken,
          })
        )
      );
    }
  }

  updateEntryNote(userId: string, cartType: CartType, cartId: string, entry: OrderEntry, note: string): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.UpdateCartEntryNote({
          note,
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  updateNameMarking(
    userId: string,
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    qualifier: string,
    ruleId: string,
    extendedIdentifier: string,
    values: string[]
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.UpdateCartEntryNameMarking({
          userId,
          cartId,
          entry,
          cartType,
          qualifier,
          ruleId,
          extendedIdentifier,
          values,
          actionToken,
        })
      )
    );
  }

  updateEntryStatisticsCode(
    userId: string,
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    statisticsCode: string
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.UpdateCartEntryStatisticsCode({
          statisticsCode,
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  updateEntryAccountingCode(
    userId: string,
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    accountingCode: string
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.UpdateCartEntryAccountingCode({
          accountingCode,
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  getUpdateEntryAccountingCode(cartId, entry: OrderEntry): Observable<AccountingCode> {
    return this.store.pipe(
      select(CartSelectors.getCartEntryAccountingCodeFactory(cartId, entry)),
      filter((accountingCode) => !!accountingCode)
    );
  }

  replaceEntry(
    userId: string,
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    articleNumber: string,
    quantity: number,
    unit: Unit
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.ReplaceCartEntry({
          userId,
          cartType,
          cartId,
          entry,
          articleNumber,
          quantity,
          unit,
          actionToken,
        })
      )
    );
  }

  addEntryFullPalletNote(userId: string, cartType: CartType, cartId: string, entry: OrderEntry): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.AddCartEntryFullPalletNote({
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  removeEntryFullPalletNote(userId: string, cartType: CartType, cartId: string, entry: OrderEntry): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.RemoveCartEntryFullPalletNote({
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  addEntryCutting(
    userId: string,
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    cutting: CuttingRequest
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.isAddCartEntryAllowed(cartId)
        .subscribe(() => {
          this.store.dispatch(
            new CartActions.AddCartEntryCutting({
              userId,
              cartType,
              cartId,
              entry,
              cutting,
              actionToken,
            })
          );
        })
        .unsubscribe()
    );
  }

  removeEntryCutting(userId: string, cartType: CartType, cartId: string, entry: OrderEntry): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.RemoveCartEntryCutting({
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  addEntryReelCutting(
    userId: string,
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    reelCutting: ReelCuttingRequest
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.isAddCartEntryAllowed(cartId)
        .subscribe(() => {
          this.store.dispatch(
            new CartActions.AddCartEntryReelCutting({
              userId,
              cartType,
              cartId,
              entry,
              reelCutting,
              actionToken,
            })
          );
        })
        .unsubscribe()
    );
  }

  removeEntryReelCutting(userId: string, cartType: CartType, cartId: string, entry: OrderEntry): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.RemoveCartEntryReelCutting({
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  addEntryReaming(userId: string, cartType: CartType, cartId: string, entry: OrderEntry): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.isAddCartEntryAllowed(cartId)
        .subscribe(() => {
          this.store.dispatch(
            new CartActions.AddCartEntryReaming({
              userId,
              cartType,
              cartId,
              entry,
              actionToken,
            })
          );
        })
        .unsubscribe()
    );
  }

  removeEntryReaming(userId: string, cartType: CartType, cartId: string, entry: OrderEntry): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.RemoveCartEntryReaming({
          userId,
          cartType,
          cartId,
          entry,
          actionToken,
        })
      )
    );
  }

  updateEntryPalletFlags(
    userId: string,
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    palletFlags: PalletFlagsRequest
  ): Observable<boolean> {
    return this.asyncReturnService.asyncReturnSerializable((actionToken) =>
      this.store.dispatch(
        new CartActions.UpdateCartEntryPalletFlags({
          userId,
          cartType,
          cartId,
          entry,
          palletFlags,
          actionToken,
        })
      )
    );
  }

  getLastEntry(cartId: string, articleCode: string, quantity: number, unit: Unit): Observable<OrderEntry | null> {
    return this.store.pipe(
      select(CartSelectors.getCartEntriesFactory(cartId)),
      map((entries) => {
        const filteredEntries = entries.filter(
          (entry) => entry.articleRef === articleCode && entry.quantity === quantity && entry.unit?.code === unit.code
        );
        return filteredEntries ? filteredEntries[filteredEntries.length - 1] : undefined;
      })
    );
  }

  private isAddCartEntryAllowed(cartId: string, entriesToAddCount: number = 1): Observable<boolean> {
    return this.validateMaxOrderLines(cartId, entriesToAddCount).pipe(
      tap((isValid) => {
        if (!isValid) {
          this.eventService.dispatch(new AddCartEntriesFailEvent());

          this.globalMessageService.add(
            {
              key: 'cart.addCartEntryFailureMaxOrderLinesExceeded_message',
            },
            GlobalMessageType.MSG_TYPE_WARNING
          );
        }
      }),
      filter((isValid) => isValid)
    );
  }

  private validateMaxOrderLines(cartId, entriesToAddCount: number = 1): Observable<boolean> {
    return this.getEntries(cartId).pipe(
      withLatestFrom(this.principalConfigurationService.getValue('maxOrderlinesPerOrder')),
      take(1),
      map(([entries, maxOrderLines]) =>
        maxOrderLines?.value && entries?.length ? entries.length + entriesToAddCount <= Number(maxOrderLines.value) : true
      )
    );
  }

  isMaxOrderLinesExceeded(cartId): Observable<boolean> {
    return this.getEntries(cartId).pipe(
      withLatestFrom(this.principalConfigurationService.getValue('maxOrderlinesPerOrder')),
      map(([entries, maxOrderLines]) =>
        maxOrderLines?.value && entries?.length ? entries.length >= Number(maxOrderLines.value) : false
      )
    );
  }
}
